import { ApiError } from "@/lib/errors";
import { resilientFetch } from "@/lib/resilient-fetch";
import type { SubjectClaims } from "@/lib/taxos-api";

const TAXOS_API_BASE = "/taxos-api";

export type LoginRequest = {
  email: string;
  password: string;
};

export type RefreshRequest = {
  refreshToken: string;
};

export type LogoutRequest = {
  refreshToken: string;
};

export type ChangePasswordRequest = {
  currentPassword: string;
  newPassword: string;
  confirmPassword?: string;
};

// Email and display name are resolved from the invite server-side; only the password is the
// invitee's to provide.
export type AcceptInviteRequest = {
  inviteToken: string;
  password: string;
};

export type InvitePreview = {
  maskedEmail: string;
  displayName: string | null;
  organizationName: string | null;
  inviteRole: string | null;
  advisorInvite: boolean;
  /** The invited email already has a TaxOS account with a password — the form asks for THAT
   * password instead of setting a new one. */
  existingAccount: boolean;
};

export async function getInvitePreview(token: string): Promise<InvitePreview> {
  const response = await fetch(`${TAXOS_API_BASE}/api/v1/auth/invites/${encodeURIComponent(token)}`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw await authApiError(response);
  }
  return (await response.json()) as InvitePreview;
}

// Minimal body - the backend accepts org name, slug and KRA verification as optional;
// the frontend defers all of them to onboarding and sends only credentials + DPA consent.
export type SelfServeSignupRequest = {
  email: string;
  password: string;
  acceptedProcessing: boolean;
};

export type AuthTokenResponse = {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
  firstLogin?: boolean;
  claims?: SubjectClaims;
};

function normalizeTokenResponse(raw: Record<string, unknown>): AuthTokenResponse {
  const accessToken = String(raw.accessToken ?? raw.access_token ?? "");
  const refreshToken = String(raw.refreshToken ?? raw.refresh_token ?? "");
  const expiresRaw = raw.expiresIn ?? raw.expires_in;
  return {
    accessToken,
    refreshToken,
    expiresIn: expiresRaw === undefined ? undefined : Number(expiresRaw),
    firstLogin: Boolean(raw.firstLogin ?? raw.first_login),
    claims: raw.claims as SubjectClaims | undefined,
  };
}

/** Builds a typed ApiError from an auth endpoint's error response (message ?? error ?? detail). */
async function authApiError(response: Response): Promise<ApiError> {
  const fallback = `Request failed with HTTP ${response.status}`;
  const text = await response.text();
  if (!text) {
    return new ApiError({ status: response.status, message: fallback });
  }
  try {
    const json = JSON.parse(text) as Record<string, unknown>;
    const message = json.message ?? json.error ?? json.detail;
    return new ApiError({
      status: response.status,
      message: typeof message === "string" ? message : text,
      code: typeof json.error === "string" ? json.error : undefined,
      correlationId: typeof json.correlationId === "string" ? json.correlationId : undefined,
    });
  } catch {
    return new ApiError({ status: response.status, message: text });
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${TAXOS_API_BASE}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw await authApiError(response);
  }

  const text = await response.text();
  return (text ? JSON.parse(text) : {}) as T;
}

export async function loginAuth(body: LoginRequest): Promise<AuthTokenResponse> {
  const raw = await postJson<Record<string, unknown>>("/api/v1/auth/login", body);
  return normalizeTokenResponse(raw);
}

export async function acceptInviteAuth(body: AcceptInviteRequest): Promise<AuthTokenResponse> {
  const raw = await postJson<Record<string, unknown>>("/api/v1/auth/accept-invite", body);
  return normalizeTokenResponse(raw);
}

// Password reset (backend: AuthController#forgotPassword / #resetPassword, both 204).
export async function forgotPasswordAuth(email: string): Promise<void> {
  await postJson<Record<string, unknown>>("/api/v1/auth/forgot-password", { email });
}

export async function resetPasswordAuth(token: string, newPassword: string): Promise<void> {
  await postJson<Record<string, unknown>>("/api/v1/auth/reset-password", { token, newPassword });
}

// Email verification for the password-register flow - consumes the emailed token (backend 204).
export async function verifyEmailAuth(token: string): Promise<void> {
  await postJson<Record<string, unknown>>("/api/v1/auth/verify-email", { token });
}

export async function initiateSelfServeSignup(body: SelfServeSignupRequest): Promise<Record<string, unknown>> {
  return postJson<Record<string, unknown>>("/api/v1/auth/self-serve-signup/initialize", body);
}

export async function verifySelfServeSignup(verificationToken: string): Promise<AuthTokenResponse> {
  const response = await fetch(`${TAXOS_API_BASE}/api/v1/auth/self-serve-signup/verify?token=${encodeURIComponent(verificationToken)}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw await authApiError(response);
  }

  const raw = await response.json() as Record<string, unknown>;
  return normalizeTokenResponse(raw);
}


// SSO providers available for "Continue with ..." buttons (backend: OAuthController#providers).
// FAIL-CLOSED: any fetch/parse failure reads as "no providers" so the buttons simply don't render.
export type OAuthProviders = {
  google: boolean;
};

export async function getOAuthProviders(): Promise<OAuthProviders> {
  try {
    const response = await fetch(`${TAXOS_API_BASE}/api/v1/auth/oauth/providers`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      return { google: false };
    }
    const raw = (await response.json()) as Record<string, unknown>;
    return { google: raw.google === true };
  } catch {
    return { google: false };
  }
}

// Full-page navigation target (NOT an XHR): the backend 302s to Google's consent screen, and after
// the callback 302s back to /oauth/complete with tokens in the URL fragment. 404s when unconfigured.
export function googleOAuthInitUrl(): string {
  return `${TAXOS_API_BASE}/api/v1/auth/oauth/google/init`;
}

// The refresh token is normally the HttpOnly cookie (sent automatically, same-origin). A legacy/OAuth
// session may still pass one in the body; the server prefers the cookie when both are present.
export async function refreshAuthTokens(refreshToken?: string): Promise<AuthTokenResponse> {
  // Resilient: a token refresh that lands mid-deploy (gateway 502/503/504) is retried so the session
  // survives a restart. A definitive 401/403 still throws — the caller treats that as a real expiry.
  const response = await resilientFetch(`${TAXOS_API_BASE}/api/v1/auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(refreshToken ? { refreshToken } : {}),
  });

  if (!response.ok) {
    throw await authApiError(response);
  }
  const text = await response.text();
  return normalizeTokenResponse(text ? JSON.parse(text) : {});
}

export async function logoutAuth(refreshToken?: string): Promise<void> {
  const response = await fetch(`${TAXOS_API_BASE}/api/v1/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(refreshToken ? ({ refreshToken } satisfies LogoutRequest) : {}),
  });

  if (!response.ok && response.status !== 204) {
    throw await authApiError(response);
  }
}

export async function changePasswordAuth(body: ChangePasswordRequest, accessToken: string): Promise<void> {
  const response = await fetch(`${TAXOS_API_BASE}/api/v1/auth/change-password`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw await authApiError(response);
  }
}

export type SessionTenantRow = {
  tenantPublicId: string;
  tenantName: string;
  tenantSlug: string;
  membershipStatus: string;
};

export async function getSessionTenants(accessToken: string): Promise<SessionTenantRow[]> {
  const response = await fetch(`${TAXOS_API_BASE}/api/v1/session/tenants`, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw await authApiError(response);
  }

  return response.json();
}
